/**
 * SubscriptionList.js
 * Component for displaying and managing the list of subscriptions
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, 
  Table, 
  Form, 
  Row, 
  Col, 
  Badge, 
  Pagination,
  Alert,
  Spinner
} from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { subscriptionService, rollCallService, commonService } from '../../services/api';
import ExportCSVButton from '../Common/ExportCSVButton';
import { exportToCSV } from '../../utils/csvExport';
import { isPayPerUseOffer } from '../../helpers/subscriptionHelpers';
import { getAcademicYearOptions } from '../../helpers/timeHelper';

const SubscriptionList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Filters
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    academicYear: searchParams.get('academicYear') || '',
    excludeTrials: searchParams.get('excludeTrials') === 'true',
    page: parseInt(searchParams.get('page') || '1', 10),
    limit: parseInt(searchParams.get('limit') || '20', 10)
  });

  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [loadingAcademicYear, setLoadingAcademicYear] = useState(true);

  // Load current academic year
  useEffect(() => {
    let isMounted = true;
    const fetchCurrentYear = async () => {
      try {
        const response = await commonService.getCurrentAcademicYear();
        if (isMounted && response?.success) {
          setCurrentAcademicYear(response.data);
          setFilters(prev => {
            if (prev.academicYear === 'current') {
              return { ...prev, academicYear: String(response.data) };
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Error fetching current academic year:', err);
      } finally {
        if (isMounted) {
          setLoadingAcademicYear(false);
        }
      }
    };
    fetchCurrentYear();
    return () => {
      isMounted = false;
    };
  }, []);

  const academicYearOptions = useMemo(() => {
    return getAcademicYearOptions(currentAcademicYear);
  }, [currentAcademicYear]);

  // Export state
  const [exporting, setExporting] = useState(false);

  // State per mappare subscription ID a status ingressi (pay-per-use)
  const [entriesStatusMap, setEntriesStatusMap] = useState({});

  // Subscription per cui lo status ingressi è già stato richiesto (evita richieste duplicate e loop di retry)
  const requestedEntriesRef = useRef(new Set());

  // Load subscriptions
  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await subscriptionService.getSubscriptions(filters);
      
      if (response.success) {
        setSubscriptions(response.data.items || []);
        setPagination({
          currentPage: response.data.page || 1,
          totalPages: response.data.totalPages || 1,
          totalItems: response.data.totalCount || 0,
          itemsPerPage: filters.limit || 20
        });
      } else {
        setError(response.message || 'Errore nel caricamento degli abbonamenti');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading subscriptions:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Sincronizza filtri nell'URL per ripristino al back
  useEffect(() => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.academicYear) params.academicYear = String(filters.academicYear);
    if (filters.excludeTrials) params.excludeTrials = 'true';
    if (filters.page && filters.page !== 1) params.page = String(filters.page);
    if (filters.limit && filters.limit !== 20) params.limit = String(filters.limit);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Effect to load subscriptions when filters change
  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  // Determina se il filtro corrente richiede il caricamento degli ingressi
  const isEntriesFilter = useMemo(() => 
    filters.status === 'running_out' || filters.status === 'sold_out',
    [filters.status]
  );

  // Funzione per caricare status ingressi per subscription pay-per-use
  const loadEntriesStatus = useCallback(async (subs) => {
    // Carica solo le subscription mai richieste prima: una subscription già richiesta
    // (anche se in errore) non viene ricaricata automaticamente per evitare loop di richieste.
    const toLoad = (subs || []).filter(sub =>
      isPayPerUseOffer(sub?.offer) && sub.group?.id && sub.user?.id && !requestedEntriesRef.current.has(sub.id)
    );

    if (toLoad.length === 0) return;

    toLoad.forEach(sub => requestedEntriesRef.current.add(sub.id));

    setEntriesStatusMap(prev => {
      const updated = { ...prev };
      toLoad.forEach(sub => {
        updated[sub.id] = { loading: true };
      });
      return updated;
    });

    // La query restituisce lo status di tutte le subscription richieste: una sola chiamata per utente
    const subscriptionIdsByUser = toLoad.reduce((acc, sub) => {
      (acc[sub.user.id] = acc[sub.user.id] || []).push(sub.id);
      return acc;
    }, {});
    const userIds = Object.keys(subscriptionIdsByUser);

    try {
      const results = await Promise.all(
        userIds.map(userId =>
          rollCallService.getUserEntriesStatus(userId, null, subscriptionIdsByUser[userId])
        )
      );

      const resultsByUser = new Map();
      userIds.forEach((userId, index) => resultsByUser.set(userId, results[index]));

      setEntriesStatusMap(prev => {
        const updated = { ...prev };
        toLoad.forEach(sub => {
          const result = resultsByUser.get(sub.user.id);
          const specificSub = result?.success
            ? result.data?.subscriptions?.find(s => s.subscriptionId === sub.id)
            : null;

          if (specificSub) {
            updated[sub.id] = {
              loading: false,
              totalEntries: specificSub.totalEntries,
              usedEntries: specificSub.usedEntries,
              remainingEntries: specificSub.remainingEntries,
              isRunningOut: specificSub.isRunningOut,
              hasActiveMonthlySubscription: result.data.hasActiveMonthlySubscription,
              academicYear: result.data.academicYear
            };
          } else {
            updated[sub.id] = { loading: false, error: true };
          }
        });
        return updated;
      });
    } catch (err) {
      console.error('Error loading entries status:', err);
      setEntriesStatusMap(prev => {
        const updated = { ...prev };
        toLoad.forEach(sub => {
          updated[sub.id] = { loading: false, error: true };
        });
        return updated;
      });
    }
  }, []);

  // Carica status ingressi quando le subscription cambiano e il filtro è running_out/sold_out
  useEffect(() => {
    if (isEntriesFilter && subscriptions.length > 0) {
      loadEntriesStatus(subscriptions);
    }
  }, [subscriptions, isEntriesFilter, loadEntriesStatus]);

  // Reset entriesStatusMap quando il filtro cambia
  useEffect(() => {
    requestedEntriesRef.current = new Set();
    setEntriesStatusMap({});
  }, [filters.status]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    setExporting(true);
    setError('');
    
    try {
      const response = await subscriptionService.getSubscriptions({
        ...filters,
        exportAll: true
      });
      
      if (response.success && response.data.items) {
        const columns = [
          { label: 'Utente', accessor: (s) => `${s.user.firstName} ${s.user.lastName}` },
          { label: 'Corso', accessor: (s) => s.course.name },
          { label: 'Anno Sportivo', accessor: (s) => `${s.course.academicYear}/${s.course.academicYear + 1}` },
          { label: 'Gruppo', accessor: (s) => s.group?.name || 'Nessun gruppo' },
          { label: 'Prezzo', accessor: (s) => `€${s.price.toFixed(2)}` },
          { label: 'Data Inizio', accessor: (s) => moment(s.startDate).format('DD/MM/YYYY') },
          { label: 'Data Fine', accessor: (s) => moment(s.endDate).format('DD/MM/YYYY') },
          { label: 'Note', accessor: (s) => s.notes || '' }
        ];
        
        exportToCSV(response.data.items, 'iscrizioni', columns);
      } else {
        setError(response.message || 'Errore durante l\'esportazione');
      }
    } catch (error) {
      console.error('Export error:', error);
      setError('Errore durante l\'esportazione delle iscrizioni');
    } finally {
      setExporting(false);
    }
  };

  // Get status badge
  const getStatusBadge = (subscription) => {
    const now = moment();
    const endDate = moment(subscription.endDate);
    const daysUntilExpiry = endDate.diff(now, 'days');

    if (daysUntilExpiry < 0) {
      return <Badge bg="danger">Scaduto</Badge>;
    } else if (daysUntilExpiry <= 30) {
      return <Badge bg="warning">In scadenza ({daysUntilExpiry}gg)</Badge>;
    } else {
      return <Badge bg="success">Attivo</Badge>;
    }
  };

  // Get title based on status filter
  const getTitle = () => {
    switch (filters.status) {
      case 'expiring':
        return 'Abbonamenti in Scadenza (30 giorni)';
      case 'expired':
        return 'Abbonamenti Scaduti';
      case 'active':
        return 'Abbonamenti Attivi';
      case 'running_out':
        return 'Abbonamenti in Esaurimento';
      case 'sold_out':
        return 'Abbonamenti Esauriti';
      case 'unpaid':
        return 'Abbonamenti Non Pagati';
      default:
        return 'Tutti gli Abbonamenti';
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-tag me-2"></i>
          {getTitle()}
        </h2>
        <ExportCSVButton 
          onExport={handleExportCSV}
          loading={exporting}
          disabled={loading}
        />
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Row className="mb-3 g-2 align-items-center">
        <Col md={3} sm={6}>
          <Form.Group>
            <Form.Select
              aria-label="Filtro Stato Abbonamento"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tutti gli stati</option>
              <option value="active">Attivi</option>
              <option value="expiring">In Scadenza (30gg)</option>
              <option value="expired">Scaduti</option>
              <option value="running_out">In Esaurimento</option>
              <option value="sold_out">Esauriti</option>
              <option value="unpaid">Non Pagati</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3} sm={6}>
          <Form.Group>
            <Form.Select
              aria-label="Filtro Anno Sportivo"
              value={filters.academicYear || ''}
              onChange={(e) => handleFilterChange('academicYear', e.target.value)}
              disabled={loadingAcademicYear}
            >
              <option value="">Tutti gli anni</option>
              {academicYearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}/{year + 1}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3} sm={6} className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            id="exclude-trials"
            label="Escludi prove"
            checked={!!filters.excludeTrials}
            onChange={(e) => handleFilterChange('excludeTrials', e.target.checked)}
          />
        </Col>
        <Col xs="auto">
          {/* Reserved for future actions (export, new subscription, etc.) */}
        </Col>
      </Row>

      {/* Subscriptions Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Caricamento...</span>
              </Spinner>
            </div>
          ) : subscriptions.length === 0 ? (
            <Alert variant="info">
              Nessun abbonamento trovato.
            </Alert>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Atleta</th>
                    <th>Corso / Gruppo</th>
                    <th>Abbonamento</th>
                    {isEntriesFilter && <th className="text-center">Ingressi</th>}
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map(subscription => (
                    <tr key={subscription.id}>
                      <td>
                        <Link to={`/users/${subscription.user.id}/edit`}>
                          {subscription.user.firstName} {subscription.user.lastName}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/courses/${subscription.course.id}`}>
                          {subscription.course.name}
                        </Link>
                        <small className="text-muted ms-1">A.A. {subscription.course.academicYear}/{subscription.course.academicYear + 1}</small>
                        <br />
                        {subscription.group ? (
                          <small>
                            <Link to={`/groups/${subscription.group.id}/participants`}>
                              {subscription.group.name}
                            </Link>
                          </small>
                        ) : (
                          <small className="text-muted">Nessun gruppo</small>
                        )}
                      </td>
                      <td>
                        {subscription.offer ? (
                          <span className="fw-medium">{subscription.offer.name}</span>
                        ) : (
                          <span className="fw-medium text-info">Data Libera</span>
                        )}
                        <br />
                        <small className="d-block">{moment(subscription.startDate).format('DD/MM/YYYY')} – {moment(subscription.endDate).format('DD/MM/YYYY')}</small>
                        <span>€ {subscription.price.toFixed(2)}</span>
                      </td>
                      {isEntriesFilter && (
                        <td className="text-center">
                          {isPayPerUseOffer(subscription.offer) ? (
                            entriesStatusMap[subscription.id] ? (
                              entriesStatusMap[subscription.id].loading ? (
                                <Spinner animation="border" size="sm" />
                              ) : entriesStatusMap[subscription.id].error ? (
                                <small className="text-danger">
                                  <i className="fas fa-exclamation-circle me-1"></i>
                                  Errore
                                </small>
                              ) : (
                                <span>
                                  {entriesStatusMap[subscription.id].usedEntries >= entriesStatusMap[subscription.id].totalEntries ? (
                                    <Badge bg="danger" className="me-1">{entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi</Badge>
                                  ) : entriesStatusMap[subscription.id].isRunningOut ? (
                                    <Badge bg="warning" text="dark" className="me-1">{entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi</Badge>
                                  ) : <Badge bg="success" className="me-1">{entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi</Badge>}
                                </span>
                              )
                            ) : (
                              <Spinner animation="border" size="sm" />
                            )
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      )}
                      <td>{getStatusBadge(subscription)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Mostrando {subscriptions.length} di {pagination.totalItems} abbonamenti
                  </div>
                  <Pagination>
                    <Pagination.First 
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                    />
                    
                    {[...Array(pagination.totalPages)].map((_, index) => {
                      const page = index + 1;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.currentPage - 2 && page <= pagination.currentPage + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === pagination.currentPage}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      } else if (
                        page === pagination.currentPage - 3 ||
                        page === pagination.currentPage + 3
                      ) {
                        return <Pagination.Ellipsis key={page} disabled />;
                      }
                      return null;
                    })}

                    <Pagination.Next 
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={pagination.currentPage === pagination.totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SubscriptionList;
